// src/components/Properties/ColorPicker/ColorPicker.tsx
import React, { useState } from 'react';
import { Pipette, Check } from 'lucide-react';
import { DEFAULT_COLOR } from 'src/config';

const PRESET_COLORS = [
  { name: 'Default', value: DEFAULT_COLOR.value },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Green', value: '#10b981' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Violet', value: '#a855f7' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'Dark Gray', value: '#1f2937' },
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#ffffff' }
];

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

/**
 * ColorPicker - Icon color selection
 *
 * Phase 3.4 + Color Support:
 * - Preset color swatches
 * - Custom color input
 * - Visual feedback
 * - Hex color display
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = 'Icon Color'
}) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customColor, setCustomColor] = useState(value);

  const handlePresetClick = (color: string) => {
    onChange(color);
    setCustomColor(color);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  return (
    <div className="space-y-3">
      {/* Label */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <button
          onClick={() => setShowCustomPicker(!showCustomPicker)}
          className="flex items-center space-x-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
        >
          <Pipette size={12} />
          <span>Custom</span>
        </button>
      </div>

      {/* Preset Colors Grid */}
      <div className="grid grid-cols-6 gap-2">
        {PRESET_COLORS.map((color) => {
          const isSelected = value.toLowerCase() === color.value.toLowerCase();

          return (
            <button
              key={color.value}
              onClick={() => handlePresetClick(color.value)}
              className={`
                group relative h-9 w-full rounded-md border-2 transition-all
                ${
                  isSelected
                    ? 'scale-110 border-white ring-2 ring-blue-500 dark:border-slate-900'
                    : 'border-gray-200 hover:scale-105 hover:border-gray-300 dark:border-slate-600 dark:hover:border-slate-500'
                }
              `}
              style={{ backgroundColor: color.value }}
              title={color.name}
            >
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Check
                    size={16}
                    className="text-white drop-shadow-md"
                    strokeWidth={3}
                  />
                </div>
              )}

              {/* Hover tooltip */}
              <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-slate-700">
                {color.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom Color Picker */}
      {showCustomPicker && (
        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-slate-600 dark:bg-slate-800/50">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Custom Color
          </label>

          <div className="flex items-center space-x-2">
            {/* HTML5 Color Input */}
            <div className="relative flex-1">
              <input
                type="color"
                value={customColor}
                onChange={handleCustomColorChange}
                className="h-10 w-full cursor-pointer rounded border border-gray-300 dark:border-slate-600"
              />
            </div>

            {/* Hex Display */}
            <input
              type="text"
              value={customColor.toUpperCase()}
              onChange={(e) => {
                const hex = e.target.value;
                if (/^#[0-9A-F]{6}$/i.test(hex)) {
                  setCustomColor(hex);
                  onChange(hex);
                }
              }}
              className="w-24 rounded border border-gray-300 bg-white px-2 py-2 text-center text-xs font-mono text-gray-700 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300"
              placeholder="#3B82F6"
              maxLength={7}
            />
          </div>

          {/* Color Preview */}
          <div className="flex items-center space-x-2">
            <div
              className="h-6 w-6 rounded border-2 border-white shadow-sm ring-1 ring-gray-300 dark:border-slate-900 dark:ring-slate-600"
              style={{ backgroundColor: customColor }}
            />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Preview
            </span>
          </div>
        </div>
      )}

      {/* Selected Color Info */}
      {/* <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-slate-600 dark:bg-slate-800/50">
        <div className="flex items-center space-x-2">
          <div
            className="h-5 w-5 rounded border-2 border-white shadow-sm ring-1 ring-gray-300 dark:border-slate-900 dark:ring-slate-600"
            style={{ backgroundColor: value }}
          />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Selected:
          </span>
        </div>
        <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
          {value.toUpperCase()}
        </span>
      </div> */}
    </div>
  );
};
